import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Contact } from '../../types/database';
import { useDatabase } from '../../hooks/useDatabase';
import { fixAllBidirectionalRelationships } from '../../utils/fixRelationships';

interface ContactRelationshipsProps {
  contact: Contact;
  onUpdate: (updatedContact: Contact) => void;
}

const RELATIONSHIP_TYPES = [
  'Associate',
  'Colleague',
  'Mentor',
  'Mentee',
  'Client',
  'Partner',
  'Friend',
  'Family',
  'Other',
];

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  return `${Math.floor(diffInSeconds / 31536000)}y ago`;
};

export const ContactRelationships: React.FC<ContactRelationshipsProps> = ({
  contact,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const db = useDatabase();
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [relationshipType, setRelationshipType] = useState<string>('');
  const [showAllAssociates, setShowAllAssociates] = useState(false);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const { data, error } = await db.getContacts();
        if (error) throw error;
        setAllContacts(data || []);
      } catch (err) {
        console.error('Failed to load contacts:', err);
      }
    };
    loadContacts();
  }, [db]);

  const handleLinkContact = async () => {
    if (!selectedContact) return;

    try {
      const updatedAssociatedContacts = [...contact.associated_contacts, selectedContact.id];
      const updatedRelationshipTypes = {
        ...contact.relationship_types,
        [selectedContact.id]: relationshipType || 'Associate',
      };

      const { data: updatedContact, error } = await db.updateContact(contact.id, {
        associated_contacts: updatedAssociatedContacts,
        relationship_types: updatedRelationshipTypes,
      });

      if (error) throw error;

      const selectedContactUpdatedAssociated = [...selectedContact.associated_contacts, contact.id];
      const selectedContactUpdatedTypes = {
        ...selectedContact.relationship_types,
        [contact.id]: relationshipType || 'Associate',
      };

      const { error: selectedContactError } = await db.updateContact(selectedContact.id, {
        associated_contacts: selectedContactUpdatedAssociated,
        relationship_types: selectedContactUpdatedTypes,
      });

      if (selectedContactError) throw selectedContactError;

      if (updatedContact) {
        onUpdate(updatedContact);
      }

      setLinkDialogOpen(false);
      setSelectedContact(null);
      setRelationshipType('');
    } catch (err) {
      console.error('Failed to link contact:', err);
    }
  };

  const handleUnlinkContact = async (contactId: string) => {
    try {
      const updatedAssociatedContacts = contact.associated_contacts.filter(id => id !== contactId);
      const updatedRelationshipTypes = { ...contact.relationship_types };
      delete updatedRelationshipTypes[contactId];

      const { data: updatedContact, error } = await db.updateContact(contact.id, {
        associated_contacts: updatedAssociatedContacts,
        relationship_types: updatedRelationshipTypes,
      });

      if (error) throw error;

      const otherContact = allContacts.find(c => c.id === contactId);
      if (otherContact) {
        const otherContactUpdatedAssociated = otherContact.associated_contacts.filter(id => id !== contact.id);
        const otherContactUpdatedTypes = { ...otherContact.relationship_types };
        delete otherContactUpdatedTypes[contact.id];

        const { error: otherContactError } = await db.updateContact(contactId, {
          associated_contacts: otherContactUpdatedAssociated,
          relationship_types: otherContactUpdatedTypes,
        });

        if (otherContactError) throw otherContactError;
      }

      if (updatedContact) {
        onUpdate(updatedContact);
      }
    } catch (err) {
      console.error('Failed to unlink contact:', err);
    }
  };

  const associatedContacts = useMemo(() => {
    const filtered = allContacts.filter(c => contact.associated_contacts.includes(c.id));
    return filtered.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [allContacts, contact.associated_contacts]);

  const displayedAssociates = useMemo(() => {
    return showAllAssociates ? associatedContacts : associatedContacts.slice(0, 5);
  }, [associatedContacts, showAllAssociates]);

  const handleContactClick = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const fixBidirectionalRelationships = async () => {
    try {
      // Use the utility function to fix all relationships
      await fixAllBidirectionalRelationships(allContacts, db.updateContact);
      
      // Refresh the current contact data
      const { data: updatedContact, error } = await db.getContactById(contact.id);
      if (error) throw error;
      
      if (updatedContact) {
        onUpdate(updatedContact);
      }
    } catch (err) {
      console.error('Failed to fix bidirectional relationships:', err);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Associates</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={fixBidirectionalRelationships}
            size="small"
            color="secondary"
          >
            Update
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => setLinkDialogOpen(true)}
            disabled={allContacts.length === 0}
            size="small"
          >
            Link Contact
          </Button>
        </Box>
      </Box>

      {associatedContacts.length > 0 ? (
        <>
          <List>
            {displayedAssociates.map((associatedContact) => (
              <ListItem
                key={associatedContact.id}
                onClick={() => handleContactClick(associatedContact.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnlinkContact(associatedContact.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{associatedContact.name}</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.disabled',
                          opacity: 0.7,
                          '&::before': {
                            content: '"•"',
                            mx: 1,
                            color: 'text.disabled',
                            opacity: 0.5
                          }
                        }}
                      >
                        {contact.relationship_types[associatedContact.id] || 'Associate'}
                      </Typography>
                      <Tooltip title={new Date(associatedContact.updated_at).toLocaleString()}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.disabled',
                            opacity: 0.7,
                            '&::before': {
                              content: '"•"',
                              mx: 1,
                              color: 'text.disabled',
                              opacity: 0.5
                            }
                          }}
                        >
                          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>active </Box>
                          {formatTimeAgo(associatedContact.updated_at)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          {associatedContacts.length > 5 && (
            <Button
              onClick={() => setShowAllAssociates(!showAllAssociates)}
              sx={{ mt: 2 }}
            >
              {showAllAssociates ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No associated contacts
        </Typography>
      )}

      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedContact(null);
          setRelationshipType('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Contact</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={allContacts.filter(c => 
                c.id !== contact.id && !contact.associated_contacts.includes(c.id)
              )}
              getOptionLabel={(option) => option.name}
              value={selectedContact}
              onChange={(_, newValue) => setSelectedContact(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contact"
                  fullWidth
                />
              )}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Relationship Type (Optional)</InputLabel>
              <Select
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                label="Relationship Type (Optional)"
              >
                {RELATIONSHIP_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLinkDialogOpen(false);
              setSelectedContact(null);
              setRelationshipType('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkContact}
            variant="contained"
            disabled={!selectedContact}
          >
            Link Contact
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 